import { api } from '../lib/api';

/**
 * PlatformService - Centralizes all API calls organized by domain
 */

// ===== AUTH & USER SERVICES =====
export const authService = {
  /**
   * User login
   */
  async login(email, password) {
    const response = await api.post('/users?operation=login', { email, password });
    return response.data;
  },

  /**
   * New user registration
   */
  async register(userData) {
    const response = await api.post('/users', userData);
    return response.data;
  },

  /**
   * Get current user data or by ID
   */
  async getUser(userId = null) {
    const params = userId ? `?id=${userId}` : '';
    const response = await api.get(`/users${params}`);
    return response.data;
  }
};

// ===== AGENT SERVICES =====
export const agentService = {
  /**
   * Create new agent
   */
  async createAgent(agentData) {
    const response = await api.post('/agents', agentData);
    return response.data;
  },

  /**
   * List user agents with filters
   */
  async getUserAgents(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    const url = `/agents?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get specific agent by ID
   */
  async getAgentById(agentId) {
    const response = await api.get(`/agents/${agentId}`);
    return response.data;
  },

  /**
   * Update agent
   */
  async updateAgent(agentId, agentData) {
    const response = await api.put(`/agents/${agentId}`, agentData);
    return response.data;
  },

  /**
   * Update agent page settings
   */
  async updateAgentPageSettings(agentId, widgetSettings) {
    const response = await api.patch(`/agents/${agentId}?action=widgetSettings`, widgetSettings);
    return response.data;
  },

  /**
   * Update specific agent integration
   */
  async updateAgentIntegration(agentId, integrationId, integrationData) {
    const response = await api.patch(
      `/agents/${agentId}?action=integration&integrationId=${integrationId}`, 
      integrationData
    );
    return response.data;
  },

  /**
   * Increment agent statistic
   */
  async incrementAgentStat(agentId, statName) {
    const response = await api.patch(`/agents/${agentId}?action=stat&statName=${statName}`);
    return response.data;
  },

  /**
   * Delete agent
   */
  async deleteAgent(agentId) {
    const response = await api.delete(`/agents/${agentId}`);
    return response.data;
  }
};

// ===== SUBSCRIPTION SERVICES =====
export const subscriptionService = {
  /**
   * Get subscription summary
   */
  async getSubscriptionSummary() {
    const response = await api.get('/api/subscription/summary');
    return response.data;
  },

  /**
   * Add token package
   */
  async addTokenPackage() {
    const response = await api.post('/api/subscription/add-package');
    return response.data;
  },

  /**
   * Create Stripe Checkout Session for new subscription
   */
  async createCheckoutSession(plan) {
    const response = await api.post('/api/stripe/create-checkout-session', { 
      plan,
      success_url: `${window.location.origin}/account?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Create Stripe Customer Portal session for managing subscription
   */
  async createPortalSession() {
    const response = await api.post('/api/stripe/create-portal-session', {
      return_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Create payment for token package
   */
  async purchaseTokenPackage() {
    const response = await api.post('/api/stripe/purchase-token-package', {
      success_url: `${window.location.origin}/account?package_success=true`,
      cancel_url: `${window.location.origin}/account`
    });
    return response.data;
  },

  /**
   * Helper functions for formatting
   */
  helpers: {
    // Formatar valor em BRL
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    // Calcular percentual de uso
    calculateUsagePercentage(used, limit) {
      if (!limit || limit === 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },

    // Formatar tokens grandes
    formatTokens(tokens) {
      if (tokens >= 1000000) {
        return `${(tokens / 1000000).toFixed(2)}M`;
      } else if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(1)}K`;
      }
      return tokens.toString();
    }
  }
};



// ===== FILE SERVICES =====
export const fileService = {
  /**
   * Upload de arquivo para o Azure Blob Storage
   * @param {File} file - Arquivo a ser enviado
   * @returns {Promise<Object>} Dados do arquivo uploadado
   */
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }
};


// ===== CHAT SERVICES =====
export const chatService = {
  /**
   * Send message to chat (with streaming)
   */
  async sendMessage(chatData) {
    const response = await api.post('/chat', chatData);
    return response.data;
  },

  /**
   * List user conversations
   */
  async getAllChats(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.agent_id) queryParams.append('agent_id', params.agent_id);
    if (params.channel) queryParams.append('channel', params.channel);
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.user_id) queryParams.append('user_id', params.user_id);

    const url = `/chats?${queryParams.toString()}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get specific conversation
   */
  async getChatById(chatId) {
    const response = await api.get(`/chats/${chatId}`);
    return response.data;
  },

  /**
   * Update conversation
   */
  async updateChat(chatId, updates) {
    const response = await api.put(`/chats/${chatId}`, updates);
    return response.data;
  },

  /**
   * Delete conversation
   */
  async deleteChat(chatId, hardDelete = true) {
    const params = hardDelete ? '?hard_delete=true' : '?hard_delete=false';
    const response = await api.delete(`/chats/${chatId}${params}`);
    return response.data;
  },

  /**
   * Get messages from a conversation
   */
  async getChatMessages(chatId, limit = 100, offset = 0) {
    const response = await api.get(`/chats/${chatId}/messages?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  /**
   * Send manual message (Human in Loop)
   */
  async sendHumanMessage(chatId, content, files = null) {
    const response = await api.post(`/chats/${chatId}/human-message`, { content, files });
    return response.data;
  },

  /**
   * Get conversation state
   */
  async getChatState(chatId) {
    const response = await api.get(`/chats/${chatId}/state`);
    return response.data;
  },

  /**
   * Update conversation state
   */
  async updateChatState(chatId, state, reason = null) {
    const response = await api.put(`/chats/${chatId}/state`, { state, reason });
    return response.data;
  }
};

// ===== MESSAGE SERVICES =====
export const messageService = {
  /**
   * Get specific message
   */
  async getMessageById(messageId) {
    const response = await api.get(`/messages/${messageId}`);
    return response.data;
  },

  /**
   * Rate message (like/dislike/suggestion)
   */
  async reviewMessage(messageId, review, suggestion = null) {
    const response = await api.post(`/messages/${messageId}/review`, { review, suggestion });
    return response.data;
  }
};

export const analyticsService = {
  /**
   * Get dashboard principal com métricas agregadas
   */
  async getDashboard(period = 30, agentId = null) {
    const params = new URLSearchParams();
    params.append('period', period);
    if (agentId) params.append('agent_id', agentId);
    
    const response = await api.get(`/analytics/dashboard?${params.toString()}`);
    return response.data;
  },

  /**
   * Get ranking dos agentes mais ativos
   */
  async getAgentsRanking(period = 30, limit = 10) {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('limit', limit);
    
    const response = await api.get(`/analytics/agents-ranking?${params.toString()}`);
    return response.data;
  },

  /**
   * Get mapa de calor de horários
   */
  async getHourlyHeatmap(period = 30) {
    const params = new URLSearchParams();
    params.append('period', period);
    
    const response = await api.get(`/analytics/hourly-heatmap?${params.toString()}`);
    return response.data;
  },

  /**
   * Funções auxiliares para conversão de métricas
   */
  helpers: {
    // Converter tokens em mensagens estimadas
    tokensToMessages(tokens) {
      return Math.round(tokens / 750); // 750 tokens por mensagem média
    },

    // Converter tokens em custo estimado
    tokensToCost(inputTokens, outputTokens) {
      // REGRA DE PRECIFICAÇÃO - AGORA AGENTS
      // Margem: 30% sobre custo total do fornecedor
      const MARGEM_LUCRO = 0.30;
      const CUSTO_INPUT_MILHAO = 11.01;
      const CUSTO_OUTPUT_MILHAO = 44.04;

      const custoInput = (inputTokens / 1_000_000) * CUSTO_INPUT_MILHAO;
      const custoOutput = (outputTokens / 1_000_000) * CUSTO_OUTPUT_MILHAO;
      const custoBase = custoInput + custoOutput;
      const custoFinal = custoBase * (1 + MARGEM_LUCRO);

      return {
        total: custoFinal,
        input: custoInput * (1 + MARGEM_LUCRO),
        output: custoOutput * (1 + MARGEM_LUCRO),
        base: custoBase,
        margem: custoFinal - custoBase
      };
    },

    // Calcular economia vs atendimento humano
    calculateSavings(totalMessages) {
      const CUSTO_ATENDIMENTO_HUMANO = 5.00; // R$ por atendimento
      return totalMessages * CUSTO_ATENDIMENTO_HUMANO;
    },

    // Formatar números grandes
    formatLargeNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    }
  }
};


// ===== API KEY SERVICES =====
export const apiKeyService = {
  /**
   * List user API keys
   */
  async listApiKeys() {
    const response = await api.get('/apikeys');
    return response.data;
  },

  /**
   * Create new API key
   */
  async createApiKey(name) {
    const response = await api.post('/apikeys', { name });
    return response.data;
  },

  /**
   * Delete API key
   */
  async deleteApiKey(keyId) {
    const response = await api.delete(`/apikeys/${keyId}`);
    return response.data;
  }
};

// ===== MCP TOOLS SERVICES =====
export const mcpToolsService = {
  /**
   * Create new MCP tool
   */
  async createMcpTool(toolData) {
    const response = await api.post('/mcp-tools', toolData);
    return response.data;
  },

  /**
   * List enabled MCP tools
   */
  async getEnabledMcpTools() {
    const response = await api.get('/mcp-tools/enabled');
    return response.data;
  },

  /**
   * List all MCP tools
   */
  async listMcpTools() {
    const response = await api.get('/mcp-tools');
    return response.data;
  },

  /**
   * Get specific MCP tool
   */
  async getMcpToolById(toolId) {
    const response = await api.get(`/mcp-tools/${toolId}`);
    return response.data;
  },

  /**
   * Update MCP tool
   */
  async updateMcpTool(toolId, toolData) {
    const response = await api.put(`/mcp-tools/${toolId}`, toolData);
    return response.data;
  },

  /**
   * Delete MCP tool
   */
  async deleteMcpTool(toolId) {
    const response = await api.delete(`/mcp-tools/${toolId}`);
    return response.data;
  }
};

// ===== LEGACY SERVICES (maintained for compatibility) =====
export const legacyService = {
  /**
   * Get chat history (use chatService.getChatMessages)
   */
  async getChatHistory(chatId) {
    const params = chatId ? `?chat_id=${chatId}` : '';
    const response = await api.get(`/chat-history${params}`);
    return response.data;
  },

  /**
   * Clear chat history
   */
  async clearChatHistory(chatId) {
    const params = chatId ? `?chat_id=${chatId}` : '';
    const response = await api.delete(`/chat-history${params}`);
    return response.data;
  }
};

// ===== SYSTEM SERVICES =====
export const systemService = {
  /**
   * Check API status
   */
  async getStatus() {
    const response = await api.get('/status');
    return response.data;
  }
};

// ===== CHANNEL SERVICES =====
export const channelService = {
  /**
   * Create new channel
   */
  async createChannel(channelData) {
    const response = await api.post('/channels', channelData);
    return response.data;
  },

  /**
   * List user channels
   */
  async listChannels(agentId = null) {
    const params = agentId ? `?agent_id=${agentId}` : '';
    const response = await api.get(`/channels${params}`);
    return response.data;
  },

  /**
   * Get specific channel
   */
  async getChannel(channelId) {
    const response = await api.get(`/channels/${channelId}`);
    return response.data;
  },

  /**
   * Update channel
   */
  async updateChannel(channelId, updates) {
    const response = await api.put(`/channels/${channelId}`, updates);
    return response.data;
  },

  /**
   * Delete channel
   */
  async deleteChannel(channelId) {
    const response = await api.delete(`/channels/${channelId}`);
    return response.data;
  },

  /**
   * Test Messenger connection
   */
  async testMessengerConnection(credentials) {
    const response = await api.post('/channels/messenger/test', credentials);
    return response.data;
  },
};

// Default export maintaining compatibility
export default {
  auth: authService,
  agents: agentService,
  chats: chatService,
  messages: messageService,
  analytics: analyticsService,
  apiKeys: apiKeyService,
  mcpTools: mcpToolsService,
  legacy: legacyService,
  system: systemService,
  subscription: subscriptionService,
  channelService: channelService
};